import React from 'react';
import { Immutable, toImmutable } from 'nuclear-js';
import numeral from 'numeral';
import ReactLoader from 'utils/react-loader';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import NumberOrdinal from 'components/generic/number-ordinal';
import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import CommonStrings from 'resources/common-strings';
import { getStudentFirstName } from 'utils/students';
import GrowthUtils from './utils';
import GrowthGoalUtils from '../utils';
import TermUtils from 'utils/terms';
import GrowthGoalsDataAccessUtils from '../data-access-utils';
import SetGoalButton from './set-goal-button';
import HelpBox from 'components/generic/help/index';
import { isIE } from 'utils/features';
import { ieScrollIntoView } from 'utils/browser-compatibility';
import {
    getInstructionalAreaStrength,
    sortInstructionalAreas,
} from 'components/student-profile/modules/instructional-areas/utils';

export default CreateReactClass({
    displayName: 'GrowthGoalSettingForm',

    propTypes: {
        clearCgiWarnings: PropTypes.func.isRequired,
        clearDirtyDataFlag: PropTypes.func.isRequired,
        currentRitForSubject: PropTypes.number,
        goalSettingTerms: PropTypes.instanceOf(Immutable.List).isRequired,
        growthGoals: PropTypes.instanceOf(Immutable.List),
        growthGoalNorms: PropTypes.instanceOf(Immutable.Map),
        growthGoalNormsLoaded: PropTypes.bool.isRequired,
        growthGoalYn: PropTypes.bool.isRequired,
        highlightTerm: PropTypes.func.isRequired,
        maxTestEvent: PropTypes.instanceOf(Immutable.Map),
        percentileIfMet: PropTypes.number,
        previousRitScore: PropTypes.number,
        reinitialize: PropTypes.func.isRequired,
        ritQuintilesLoaded: PropTypes.bool.isRequired,
        setCgiWarningMessage: PropTypes.func.isRequired,
        setDirtyDataFlag: PropTypes.func.isRequired,
        showGoalSettingFailedModal: PropTypes.func.isRequired,
        showGoalAlreadySetModal: PropTypes.func.isRequired,
        startingTerm: PropTypes.instanceOf(Immutable.Map),
        studentBid: PropTypes.string.isRequired,
        studentTermData: PropTypes.instanceOf(Immutable.List).isRequired,
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        targetTermNumber: PropTypes.number,
        typicalGrowth: PropTypes.number,
        typicalScore: PropTypes.number,
        unHighlightTerm: PropTypes.func.isRequired,
        unsavedData: PropTypes.bool.isRequired,
        upcomingGoalsId: PropTypes.string,
        updateCurrentRit: PropTypes.func,
    },

    getInitialState() {
        return {
            currentRit: null,
            ritChangedYn: false,
            ritValidYn: true,
            currentGrowth: null,
            growthChangedYn: false,
            growthValidYn: true,
            currentStatusPercentile: null, // populated in componentWillMount
            statusPercentileChangedYn: false,
            statusPercentileValidYn: true,
            currentGrowthPercentile: null,
            growthPercentileChangedYn: false,
            growthPercentileValidYn: true,
            currentGrowthIndex: null,
            actionPlanTextCounter: 0,
            actionPlanTextCounterCSS: 'not-exceeded',
            currentActionPlan: '',
        };
    },

    UNSAFE_componentWillMount() {
        this.initializeState(this.props);
    },

    /*
     * Since removing the triple student and multiple module logic from Student Profile,
     * this component has been found to be a bit of a pain point for Growth Goals not updating
     * all data when changing students. If you're hunting for bugs, take a good look here.
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        // want to initialize state when first hit page, after saving or discarding changes
        const unsavedData = !nextProps.unsavedData && this.props.unsavedData;
        const targetTermNumber = nextProps.targetTermNumber !== this.props.targetTermNumber;
        const studentBid = nextProps.studentBid !== this.props.studentBid;
        const growthGoalNormsLoaded = nextProps.growthGoalNormsLoaded !== this.props.growthGoalNormsLoaded;
        const ritQuintilesLoaded = nextProps.ritQuintilesLoaded !== this.props.ritQuintilesLoaded;

        const growthGoalYn = nextProps.growthGoalYn !== this.props.growthGoalYn;
        const typicalGrowth = nextProps.typicalGrowth !== this.props.typicalGrowth;
        const typicalScore = nextProps.typicalScore !== this.props.typicalScore;
        const previousRitScore = nextProps.previousRitScore !== this.props.previousRitScore;

        const relevantPropChanged =
            unsavedData ||
            targetTermNumber ||
            studentBid ||
            growthGoalNormsLoaded ||
            ritQuintilesLoaded ||
            growthGoalYn ||
            typicalGrowth ||
            typicalScore ||
            previousRitScore;

        /* for debugging */
        // console.log(unsavedData, targetTermNumber, studentBid, growthGoalNormsLoaded, ritQuintilesLoaded, growthGoalYn, typicalGrowth, typicalScore, previousRitScore);
        if (relevantPropChanged) {
            this.initializeState(nextProps);
        }
    },

    initializeFlags() {
        this.setState({
            ritChangedYn: false,
            ritValidYn: true,
            growthChangedYn: false,
            growthValidYn: true,
            statusPercentileChangedYn: false,
            statusPercentileValidYn: true,
            growthPercentileChangedYn: false,
            growthPercentileValidYn: true,
        });
    },

    initializeState(props = this.props) {
        this.initializeFlags();
        const initialRit = GrowthGoalUtils.findInitialRitTarget(
            props.growthGoalYn,
            props.typicalGrowth,
            props.typicalScore,
            props.previousRitScore,
            props.growthGoals,
            props.targetTermNumber
        );
        const initialGrowth =
            props.growthGoalYn && props.previousRitScore && initialRit ? initialRit - props.previousRitScore : null;
        this.updateInputsForRitOrGrowthChange(props.targetTermNumber, initialRit, initialGrowth, true, false, props);
        props.clearCgiWarnings();

        this.setState({
            currentActionPlan: '',
            actionPlanTextCounter: 0,
            actionPlanTextCounterCSS: 'not-exceeded',
        });
    },

    updateInputsForRitOrGrowthChange(
        termNumber,
        rit,
        growth,
        initializingState = false,
        skipWarnings = false,
        props = this.props
    ) {
        const data = this.getGrowthGoalDataForRit(termNumber, rit, props);
        const currentStatusPercentile = data && data.get('statusPerc') ? data.get('statusPerc') : 0;
        const currentGrowthPercentile = data && data.get('cgp') ? data.get('cgp') : 0;
        const currentGrowthIndex = data && data.get('cgi') ? data.get('cgi') : 0;

        if (this.props.currentRitForSubject !== rit) {
            this.props.updateCurrentRit(rit);
        }

        this.setState({
            currentRit: rit,
            currentGrowth: growth,
            currentStatusPercentile,
            currentGrowthPercentile,
            currentGrowthIndex,
            ritChangedYn: false,
            growthChangedYn: false,
        });

        // this method is called when initializing state, but there are some tasks to skip in that case
        if (!initializingState) {
            const allValid = this.validateAllInputs(rit, currentStatusPercentile, growth, currentGrowthPercentile);

            if (!skipWarnings) {
                this.checkForCGIWarnings(currentGrowthIndex, allValid);
            }
        }
    },

    updateInputsForStatusPercentileChanges(termNumber, percentile, skipWarnings = false) {
        const data = this.getGrowthGoalDataForStatusPercentile(termNumber, percentile);
        const currentRit = data ? data.get('rit', 0) : 0;
        const currentGrowth = data && currentRit > 0 ? currentRit - this.props.previousRitScore : 0;
        const currentGrowthPercentile = data ? data.get('cgp', 0) : 0;
        const currentGrowthIndex = data ? data.get('cgi', 0) : 0;
        if (this.props.currentRitForSubject !== currentRit) {
            this.props.updateCurrentRit(currentRit);
        }
        this.setState({
            currentRit,
            currentGrowth,
            currentStatusPercentile: percentile,
            currentGrowthPercentile,
            currentGrowthIndex,
            statusPercentileChangedYn: false, // reset to not changed
        });

        const allValid = this.validateAllInputs(currentRit, percentile, currentGrowth, currentGrowthPercentile);
        if (!skipWarnings) {
            this.checkForCGIWarnings(currentGrowthIndex, allValid);
        }
    },

    updateInputsForCGPChanges(termNumber, cgp, skipWarnings = false) {
        const data = this.getGrowthGoalDataForCGP(termNumber, cgp);
        const currentRit = data ? data.get('rit', 0) : 0;
        const currentGrowth = data ? currentRit - this.props.previousRitScore : 0;
        const currentStatusPercentile = data ? data.get('statusPerc', 0) : 0;
        const currentGrowthIndex = data ? data.get('cgi', 0) : 0;
        if (this.props.currentRitForSubject !== currentRit) {
            this.props.updateCurrentRit(currentRit);
        }
        this.setState({
            currentRit,
            currentGrowth,
            currentStatusPercentile,
            currentGrowthPercentile: cgp,
            currentGrowthIndex,
            growthPercentileChangedYn: false, // reset to not changed
        });

        const allValid = this.validateAllInputs(currentRit, currentStatusPercentile, currentGrowth, cgp);
        if (!skipWarnings) {
            this.checkForCGIWarnings(currentGrowthIndex, allValid);
        }
    },

    changeRit(event) {
        this.setState({
            currentRit: event.target.value,
            ritChangedYn: true,
        });
    },

    changeGrowth(event) {
        this.setState({
            currentGrowth: event.target.value,
            growthChangedYn: true,
        });
    },

    changeStatusPercentile(event) {
        this.setState({
            currentStatusPercentile: event.target.value,
            statusPercentileChangedYn: true,
        });
    },

    changeCGP(event) {
        this.setState({
            currentGrowthPercentile: event.target.value,
            growthPercentileChangedYn: true,
        });
    },

    validateIntegerField(value, min, max) {
        const intValue = parseInt(value);
        return !isNaN(intValue) && intValue >= min && intValue <= max && intValue == parseFloat(value);
    },

    validateRit(rit) {
        const maxRit = GrowthGoalUtils.findMaxRit(this.props.growthGoalYn, this.props.previousRitScore);
        const minRit = GrowthGoalUtils.findMinRit(this.props.growthGoalYn, this.props.previousRitScore);
        const ritValidYn = this.validateIntegerField(rit, minRit, maxRit);
        this.setState({ ritValidYn });
        return ritValidYn;
    },

    validateGrowth(growth) {
        const growthValidYn = this.validateIntegerField(growth, CommonValues.MIN_GROWTH, CommonValues.MAX_GROWTH);
        this.setState({ growthValidYn });
        return growthValidYn;
    },

    validateStatusPercentile(percentile) {
        // 0 means there's no norms data, so consider it valid
        if (percentile === 0) {
            return true;
        }

        const statusPercentileValidYn = this.validateIntegerField(
            percentile,
            CommonValues.MIN_STATUS_PERCENTILE,
            CommonValues.MAX_STATUS_PERCENTILE
        );
        this.setState({ statusPercentileValidYn });
        return statusPercentileValidYn;
    },

    validateGrowthPercentile(percentile) {
        // 0 means there's no norms data, so consider it valid
        if (percentile === 0) {
            return true;
        }

        const growthPercentileValidYn = this.validateIntegerField(
            percentile,
            CommonValues.MIN_GROWTH_PERCENTILE,
            CommonValues.MAX_GROWTH_PERCENTILE
        );
        this.setState({ growthPercentileValidYn });
        return growthPercentileValidYn;
    },

    validateAllInputs(rit, statusPercentile, growth, growthPercentile) {
        // note: values should be passed into this function instead of relying on this.state, because
        //       the state is not always updated immediately
        const ritValidYn = this.validateRit(rit);
        const statusPercentileValidYn = this.validateStatusPercentile(statusPercentile);
        const growthValidYn = !this.props.growthGoalYn || this.validateGrowth(growth);
        const growthPercentileValidYn = !this.props.growthGoalYn || this.validateGrowthPercentile(growthPercentile);

        return ritValidYn && statusPercentileValidYn && growthValidYn && growthPercentileValidYn;
    },

    checkForCGIWarnings(cgi, allValid) {
        this.props.setCgiWarningMessage(GrowthGoalUtils.getCGIWarning(cgi, this.props.growthGoalYn, allValid));
    },

    blurRit(event, skipWarnings = false) {
        if (this.state.ritChangedYn) {
            if (this.validateRit(event.target.value)) {
                const rit = parseInt(event.target.value);
                const growth = rit - this.props.previousRitScore;
                this.updateInputsForRitOrGrowthChange(this.props.targetTermNumber, rit, growth, false, skipWarnings);
            }
            this.props.setDirtyDataFlag();
        }
    },

    blurGrowth(event, skipWarnings = false) {
        if (this.state.growthChangedYn) {
            if (this.validateGrowth(event.target.value)) {
                const growth = parseInt(event.target.value);
                const rit = this.props.previousRitScore + growth;
                this.updateInputsForRitOrGrowthChange(this.props.targetTermNumber, rit, growth, false, skipWarnings);
            }
            this.props.setDirtyDataFlag();
        }
    },

    blurStatusPercentile(event, skipWarnings = false) {
        if (this.state.statusPercentileChangedYn && this.validateStatusPercentile(event.target.value)) {
            this.updateInputsForStatusPercentileChanges(
                this.props.targetTermNumber,
                parseInt(event.target.value),
                skipWarnings
            );
        }
        this.props.setDirtyDataFlag();
    },

    blurCGP(event, skipWarnings = false) {
        if (this.state.growthPercentileChangedYn && this.validateGrowthPercentile(event.target.value)) {
            this.updateInputsForCGPChanges(this.props.targetTermNumber, parseInt(event.target.value), skipWarnings);
        }
        this.props.setDirtyDataFlag();
    },

    isRitValid() {
        return this.state.ritValidYn;
    },

    isGrowthValid() {
        return this.state.growthValidYn;
    },

    hideInvalidGrowthMessage() {
        // since the growth message is very similar and related to the rit message, hide
        //     if the rit message is being shown
        return this.isGrowthValid() || !this.isRitValid();
    },

    isStatusPercentileValid() {
        return this.state.statusPercentileValidYn;
    },

    isGrowthPercentileValid() {
        return this.state.growthPercentileValidYn;
    },

    areAllFieldsValid() {
        return (
            this.isRitValid() &&
            this.isGrowthValid() &&
            this.isStatusPercentileValid() &&
            this.isGrowthPercentileValid() &&
            this.state.actionPlanTextCounter <= CommonValues.actionGoalTextCounter
        );
    },

    isDataLoaded() {
        return !!this.props.ritQuintilesLoaded && !!this.props.growthGoalNormsLoaded;
    },

    getGrowthGoalDataForRit(termNumber, rit, props) {
        return !props.growthGoalNorms ? null : props.growthGoalNorms.getIn([termNumber, 'rit', rit], null);
    },

    getGrowthGoalDataForStatusPercentile(termNumber, percentile) {
        return !this.props.growthGoalNorms
            ? null
            : this.props.growthGoalNorms.getIn([termNumber, 'statusPerc', percentile], null);
    },

    getGrowthGoalDataForCGP(termNumber, cgp) {
        return !this.props.growthGoalNorms ? null : this.props.growthGoalNorms.getIn([termNumber, 'cgp', cgp], null);
    },

    onSetGoal() {
        Analytics.recordButtonClick(CommonValues.SectionIDs.growthGoals, 'set-goal');
        if (this.areAllFieldsValid()) {
            const goal = this.buildGrowthGoal();
            if (GrowthGoalUtils.goalAlreadyExists(this.props.growthGoals, goal)) {
                this.updateGoal(goal);
            } else {
                this.saveGoal(goal);
            }
        }
    },

    onGoalPersisted(goal, editYn) {
        // reset state of this form and the parent component
        this.props.reinitialize();
        this.props.clearDirtyDataFlag(); // this will trigger initializeState

        // highlight the row, then unhighlight it
        this.props.highlightTerm(goal.get('endTermNumber'), editYn);

        setTimeout(() => {
            this.props.unHighlightTerm();
        }, 2500);
    },

    onGoalPersistFailed() {
        this.props.showGoalSettingFailedModal(); //no then or catch
    },

    onGoalSaved(goal) {
        this.onGoalPersisted(goal, false);
    },

    onGoalUpdated(goal) {
        this.onGoalPersisted(goal, true);
    },

    scrollToUpcomingGrowthGoals() {
        const goalsSection = document.getElementById(this.props.upcomingGoalsId);
        if (goalsSection) {
            // this wil prevent exception in unit tests
            if (isIE()) {
                ieScrollIntoView(goalsSection);
            } else {
                goalsSection.scrollIntoView(true);
            }
        }
    },

    updateGoal(goal) {
        this.props
            .showGoalAlreadySetModal()
            .then(() => {
                GrowthGoalsDataAccessUtils.updateGrowthGoal(
                    this.props.studentBid,
                    goal,
                    this.props.growthGoals,
                    this.props.maxTestEvent.get('measurementScaleID'),
                    this.onGoalUpdated,
                    this.onGoalPersistFailed
                );
                //delay added below is a work around to fix issue of scrollIntoView not working when modal dialog is used
                setTimeout(() => {
                    this.scrollToUpcomingGrowthGoals();
                }, 300);
            })
            .catch(() => {}); //add blank function to prevent exception
    },

    saveGoal(goal) {
        /* :CSN I have tested that this.props.maxTestEvent is equal to the keystoneTestEvent
         * Currently not refactoring this component to make that more apparrent
         * because the prop/state logic looks very fragile */
        const courseSpecificBid = this.props.maxTestEvent.get('measurementScaleID');
        GrowthGoalsDataAccessUtils.saveGrowthGoal(
            this.props.studentBid,
            courseSpecificBid,
            goal,
            this.onGoalSaved,
            this.onGoalPersistFailed
        );
        this.scrollToUpcomingGrowthGoals();
    },

    buildGrowthGoal() {
        const endTerm = this.props.studentTermData.find(
            (t) => t.get('termNumber') === this.props.targetTermNumber,
            null,
            null
        );
        const endTermScore = GrowthGoalUtils.findEndTermScore(
            this.props.growthGoals,
            this.props.maxTestEvent,
            this.props.targetTermNumber
        );

        return toImmutable({
            endTermNumber: this.props.targetTermNumber,
            endTermName: endTerm ? endTerm.get('name') : null,
            endTermGrade: endTerm ? endTerm.get('grade') : null,
            endTermWeeksOfInstruction: endTerm ? endTerm.get('weeks') : null,
            startTermNumber: this.props.growthGoalYn ? this.props.startingTerm.get('termNumber') : null,
            startTermName: this.props.growthGoalYn ? this.props.startingTerm.get('name') : null,
            startTermGrade: this.props.growthGoalYn ? this.props.startingTerm.get('grade') : null,
            startTermWeeksOfInstruction: this.props.growthGoalYn ? this.props.startingTerm.get('weeks') : null,
            targetScore: this.state.currentRit,
            typicalScore: this.props.typicalScore,
            startTermScore: this.props.previousRitScore,
            predictedGrowth: this.props.typicalGrowth,
            actionPlan: encodeURI(this.state.currentActionPlan),
            endTermScore,
        });
    },

    getSetGoalButtonId() {
        if (!this.props.subject) {
            return null;
        }

        return `set-goal-for-subject-${this.props.subject.get('bid')}`;
    },

    wrapBlurFunction(blurFn) {
        return (event) => {
            // skip the warnings when blur was triggered by user clickin the Set Goal button
            let skipWarnings = false;

            // Firefox throws an exception in trying to read event.relatedTarget.id, but we assume that means
            //      the relatedTarget is not the setGoalButton
            try {
                skipWarnings = !!event.relatedTarget && event.relatedTarget.id === this.getSetGoalButtonId();
            } catch (e) {
                // swallow exception
            }
            blurFn(event, skipWarnings);
        };
    },

    renderInputField(label, value, ref, changeFn, blurFn, min, max, isValidFn, invalidErrorMessage, disabled = false) {
        const valid = isValidFn();
        const errorClass = valid ? '' : ' goal-input-error';

        const inputId = `${ref}-input`;

        // If value is null or undefined, you get a warning message (which happens if rendering before values all set),
        //      so convert value to empty string.  If value is 0, do not convert is, so that the UI will display 0.
        const valueToUse = value || value === 0 ? value : '';
        const input = disabled ? (
            <input
                key={inputId}
                id={inputId}
                className={`goal-input${errorClass}`}
                ref={ref}
                type="number"
                min={min} // if null, React will omit this property
                max={max}
                value=""
                disabled={disabled}
            />
        ) : (
            <input
                key={inputId}
                id={inputId}
                className={`goal-input${errorClass}`}
                ref={ref}
                type="number"
                value={valueToUse}
                min={min} // if null, React will omit this property
                max={max}
                disabled={disabled}
                onBlur={this.wrapBlurFunction(blurFn)}
                onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                        blurFn(event);
                    }
                }}
                onChange={changeFn}
            />
        );

        const errorMessage = this.renderErrorMessage(invalidErrorMessage, valid, ref);
        return errorMessage.concat([
            <label
                key={`${ref}-label`}
                id={`${ref}-label`}
                className={errorClass + (disabled ? ' disabled' : '')}
                htmlFor={inputId}
            >
                {label}
            </label>,
            input,
        ]);
    },

    renderErrorMessage(message, validYn, fieldName) {
        const id = `${fieldName}-error-message`;
        const showErrorClass = validYn ? '' : 'show-error';
        return [
            <i className={`icon icon-form-error ${showErrorClass}`} key={`error-icon-${fieldName}`} />,
            <p id={id} key={id} className={`input-error-field ${showErrorClass}`}>
                {message}
            </p>,
        ];
    },

    ritInputsHelpText() {
        return (
            <div key="growth-goal-rit-score-help-content">
                <p key="initial-text" className="help-box-p help-text">
                    {CommonStrings.goalRitScoreSettingHelp.initialText}
                </p>
                <p key="paragraph2-text" className="help-box-p help-text">
                    {CommonStrings.goalRitScoreSettingHelp.paragraph2Text}
                </p>
                <div key="help-link-section">
                    <strong key="help-read-more">{CommonStrings.goalRitScoreSettingHelp.readMoreText}</strong>
                    <a key="help-link" href={CommonStrings.goalRitScoreSettingHelp.helpLink} target="_blank">
                        {CommonStrings.goalRitScoreSettingHelp.helpText}
                    </a>
                </div>
            </div>
        );
    },

    renderRitInputs() {
        const helpBox = (
            <HelpBox
                id="growth-goal-help-box-rit-score-help"
                appLevelCssClassName=""
                helpContentsRenderer={this.ritInputsHelpText}
                moduleName={CommonValues.SectionIDs.growthGoals}
                helpLabel="custom-goals-rit-score-help"
            />
        );

        const maxRit = GrowthGoalUtils.findMaxRit(this.props.growthGoalYn, this.props.previousRitScore);
        const minRit = GrowthGoalUtils.findMinRit(this.props.growthGoalYn, this.props.previousRitScore);
        const title = <h4 key="rit-title">RIT Scores {helpBox}</h4>;
        const ritInvalidMessage = `Please enter a number between ${minRit} and ${maxRit}`;
        const ritFields = this.renderInputField(
            'Goal RIT score',
            this.state.currentRit,
            'rit',
            this.changeRit,
            this.blurRit,
            minRit,
            maxRit,
            this.isRitValid,
            ritInvalidMessage
        );

        let growthFields = [];
        let className = 'input-section';
        if (this.props.growthGoalYn) {
            const growthInvalidMessage = `Please enter a number between ${CommonValues.MIN_GROWTH} and ${CommonValues.MAX_GROWTH}`;
            growthFields = this.renderInputField(
                'RIT growth',
                this.state.currentGrowth,
                'growth',
                this.changeGrowth,
                this.blurGrowth,
                CommonValues.MIN_GROWTH,
                CommonValues.MAX_GROWTH,
                this.hideInvalidGrowthMessage,
                growthInvalidMessage
            );
        } else {
            className += ' performance-goal-setting';
        }

        if (!this.areAllFieldsValid()) {
            className += ' show-error';
        }

        return <div className={className}>{[title].concat(ritFields).concat(growthFields)}</div>;
    },

    PercentileInputsHelpText() {
        return (
            <div key="growth-goal-percentile-help-content">
                <p key="initial-text" className="help-box-p help-text">
                    {CommonStrings.goalPercentileSettingHelp.initialText}
                </p>
                <p key="paragraph2-text" className="help-box-p help-text">
                    {CommonStrings.goalPercentileSettingHelp.paragraph2Text}
                </p>
                <p key="paragraph3-text" className="help-box-p help-text">
                    {CommonStrings.goalPercentileSettingHelp.paragraph3Text}
                </p>
                <div key="help-link-section">
                    <strong key="help-read-more">{CommonStrings.goalPercentileSettingHelp.readMoreText}</strong>
                    <a key="help-link" href={CommonStrings.goalPercentileSettingHelp.helpLink} target="_blank">
                        {CommonStrings.goalPercentileSettingHelp.helpText}
                    </a>
                </div>
            </div>
        );
    },

    renderPercentileInputs() {
        const helpBox = (
            <HelpBox
                id="growth-goal-help-box-percentile-help"
                appLevelCssClassName=""
                helpContentsRenderer={this.PercentileInputsHelpText}
                moduleName={CommonValues.SectionIDs.growthGoals}
                helpLabel="custom-goals-percentiles-help"
            />
        );
        const title = <h4 key="percentile-title">Percentiles {helpBox}</h4>;
        const statusPercentileInvalidMessage = `Please enter a number between ${CommonValues.MIN_STATUS_PERCENTILE} and ${CommonValues.MAX_STATUS_PERCENTILE}`;
        const statusPercentileDisabled = this.state.currentStatusPercentile === 0;
        const percentileFields = this.renderInputField(
            'Achievement percentile',
            this.state.currentStatusPercentile,
            'statusPercentile',
            this.changeStatusPercentile,
            this.blurStatusPercentile,
            CommonValues.MIN_STATUS_PERCENTILE,
            CommonValues.MAX_STATUS_PERCENTILE,
            this.isStatusPercentileValid,
            statusPercentileInvalidMessage,
            statusPercentileDisabled
        );

        let growthPercentileFields = [];
        let growthIndexField = [];
        let className = 'input-section';
        if (this.props.growthGoalYn) {
            const growthPercentileInvalidMessage = `Please enter a number between ${CommonValues.MIN_GROWTH_PERCENTILE} and ${CommonValues.MAX_GROWTH_PERCENTILE}`;
            const growthPercentileDisabled = this.state.currentGrowthPercentile === 0;
            growthPercentileFields = this.renderInputField(
                'Growth percentile',
                this.state.currentGrowthPercentile,
                'growthPercentile',
                this.changeCGP,
                this.blurCGP,
                CommonValues.MIN_GROWTH_PERCENTILE,
                CommonValues.MAX_GROWTH_PERCENTILE,
                this.isGrowthPercentileValid,
                growthPercentileInvalidMessage,
                growthPercentileDisabled
            );
            const growthIndex = !this.state.currentGrowthIndex
                ? `${CommonStrings.space}${CommonStrings.blanks}*`
                : numeral(this.state.currentGrowthIndex).format('0.00');
            growthIndexField = <p key="cgi">Conditional Growth Index: {growthIndex}</p>;
        } else {
            className += ' performance-goal-setting';
        }

        if (!this.areAllFieldsValid()) {
            className += ' show-error';
        }

        return (
            <div className={className}>
                {[title]
                    .concat(percentileFields)
                    .concat(growthPercentileFields)
                    .concat(growthIndexField)}
            </div>
        );
    },

    renderRitComparisons() {
        if (this.props.growthGoalYn) {
            const scoreIfGrowthMet = this.props.typicalGrowth
                ? this.props.previousRitScore + this.props.typicalGrowth
                : null;

            return (
                <div key="comparison-section" className="comparisons-section">
                    <p key="rit">
                        <span className="comparison-label">RIT score if projected growth is met</span>
                        <span className="value-align">
                            {!scoreIfGrowthMet || scoreIfGrowthMet === 0
                                ? `${CommonStrings.blanks}*`
                                : scoreIfGrowthMet}
                        </span>
                    </p>
                    <p key="typical-growth">
                        <span className="comparison-label">Projected growth</span>
                        <span className="value-align">
                            {!this.props.typicalGrowth || this.props.typicalGrowth === 0
                                ? `${CommonStrings.blanks}*`
                                : this.props.typicalGrowth}
                        </span>
                    </p>
                    <p key="typical-score">
                        <span className="comparison-label">Average achievement</span>
                        <span className="value-align">
                            {!this.props.typicalScore || this.props.typicalScore === 0
                                ? `${CommonStrings.blanks}*`
                                : this.props.typicalScore}
                        </span>
                    </p>
                </div>
            );
        } else {
            return (
                <div key="comparison-section-no-prev-goals" className="comparisons-section">
                    <p>
                        <span className="comparison-label" key="label">
                            Average achievement
                        </span>
                        <span className="value-align" key="value">
                            {!this.props.typicalScore || this.props.typicalScore === 0
                                ? `${CommonStrings.blanks}*`
                                : this.props.typicalScore}
                        </span>
                    </p>
                </div>
            );
        }
    },

    renderPercentileComparisons() {
        if (this.props.growthGoalYn) {
            let percentile = null;

            if (this.props.percentileIfMet && this.props.percentileIfMet > 0) {
                percentile = <NumberOrdinal value={this.props.percentileIfMet} />;
            } else {
                percentile = `${CommonStrings.space}${CommonStrings.blanks}*`;
            }

            return (
                <div key="percentile-comparison-section" className="comparisons-section">
                    <p>
                        <span className="comparison-label">Achievement percentile if projected growth is met</span>
                        <span className="value-align">{percentile}</span>
                    </p>
                </div>
            );
        } else {
            return <div key="percentile-comparison-section" className="comparisons-section" />;
        }
    },

    renderIncompleteNormMessage() {
        const missingNorm = GrowthUtils.missingNormDataYn(
            this.props.growthGoalYn,
            this.props.percentileIfMet,
            this.props.typicalGrowth,
            this.props.typicalScore
        );

        return missingNorm ? (
            <div className="incomplete-norm-message">
                {CommonStrings.incompleteNormMessage.incompleteNormMessage1}
                <a href={CommonStrings.incompleteNormMessage.incompleteNormLink1} target="_blank">
                    {CommonStrings.incompleteNormMessage.incompleteNormLink1Text}
                </a>
                {CommonStrings.incompleteNormMessage.incompleteNormMessage2}
                <a href={CommonStrings.incompleteNormMessage.incompleteNormLink2} target="_blank">
                    {CommonStrings.incompleteNormMessage.incompleteNormLink2Text}
                </a>
                {CommonStrings.incompleteNormMessage.incompleteNormMessage3}
                {getStudentFirstName(this.props.studentBid)}.
            </div>
        ) : null;
    },

    renderSetGoalButton() {
        // !this.props.growthGoalYn is added to fix TP-767713
        return (
            <SetGoalButton
                buttonId={this.getSetGoalButtonId()}
                onClick={this.onSetGoal}
                testScoresMissing={!this.props.maxTestEvent}
                disabled={!this.areAllFieldsValid() || !this.props.maxTestEvent || !this.props.growthGoalYn}
                ref="setGoal"
            />
        );
    },

    renderNoGoalSettingTerms() {
        return (
            <div className="goal-setting-form">
                <p className="no-goal-setting-terms">{CommonStrings.noGoalSettingTerms}</p>
            </div>
        );
    },

    textCounter: function(e) {
        let currentText = e.target.value;

        const exceededClass = currentText.length > 3800 ? 'exceeded' : 'not-exceeded';

        this.setState({
            actionPlanTextCounter: currentText.length,
            actionPlanTextCounterCSS: exceededClass,
            currentActionPlan: currentText,
        });

        this.props.setDirtyDataFlag();
    },

    renderActionPlan() {
        let instructionalPanel = null;

        if (this.props.maxTestEvent && this.props.maxTestEvent.size > 0 && this.props.growthGoalYn) {
            const goals = [];
            const instructionalAreas = sortInstructionalAreas(this.props.maxTestEvent);

            instructionalAreas.forEach((goal) => {
                const instructionalAreaStrength = getInstructionalAreaStrength(
                    this.props.maxTestEvent.get('score'),
                    this.props.maxTestEvent.get('standardError'),
                    goal.get('score'),
                    goal.get('standardError')
                );

                const strength =
                    instructionalAreaStrength != '' ? (
                        <span className="icon-focus ia-strength">
                            {CommonStrings.space + CommonStrings.space + instructionalAreaStrength}
                        </span>
                    ) : null;

                goals.push(
                    <p key={goal.get('goalkey')}>
                        <span className="goal">{goal.get('name')}</span>
                        <span className="score">{goal.get('score')}</span>
                        <br />
                        {strength}
                    </p>
                );
            });

            const startTermName = TermUtils.textFromTermNumber(this.props.maxTestEvent.get('termNumber'), true);

            // We have a nested <div> explicitly to avoid instructional area grey background
            // getting rendered along with the resizing of textarea vertically.
            instructionalPanel = (
                <div className="action-plan-ia-panel">
                    <div className="action-plan-ia">
                        <h4>Instructional Area Scores - {startTermName}</h4>
                        {goals}
                    </div>
                </div>
            );
        }

        const textArea = (
            <textarea
                key="action-plan-entry"
                className={instructionalPanel ? null : 'width-no-goals'}
                onChange={this.textCounter}
                placeholder="What actions will be taken to achieve this growth?"
                value={this.state.currentActionPlan}
            ></textarea>
        );

        return (
            <div className="action-plan-section">
                <div className="layout">
                    <div
                        className={`textarea-container ${instructionalPanel ? 'with-instructional-panel' : 'without-instructional-panel'
                            }`}
                    >
                        {textArea}
                        <div
                            className={
                                instructionalPanel
                                    ? this.state.actionPlanTextCounterCSS + ' counter half-width'
                                    : this.state.actionPlanTextCounterCSS + ' counter full-width'
                            }
                        >
                            {this.state.actionPlanTextCounter}/{CommonValues.actionGoalTextCounter}
                        </div>
                    </div>
                    <div className="instructional-panel">
                        {instructionalPanel}
                    </div>
                </div>
            </div>
        );
    },

    renderActionPlanDivider() {
        if (this.props.growthGoalYn) {
            return (
                <p key="action-plan-section-divider" className="action-plan-section dividerGrowthGoalYn">
                    &nbsp;
                </p>
            );
        } else {
            return (
                <p key="action-plan-section-divider" className="action-plan-section dividerGrowthGoal">
                    &nbsp;
                </p>
            );
        }
    },

    render() {
        if (!this.props.goalSettingTerms || this.props.goalSettingTerms.isEmpty()) {
            return this.renderNoGoalSettingTerms();
        }

        const ritInputs = this.renderRitInputs();
        const percentileInputs = this.renderPercentileInputs();
        const performanceMessage = !this.props.growthGoalYn ? (
            <p key="goal-performance-text" className="goal-performance-text" id={`goal-performance-text`}>
                {CommonStrings.performanceGoalSettingMessage}
            </p>
        ) : null;
        const ritComparisons = this.renderRitComparisons();
        const percentileComparisons = this.renderPercentileComparisons();
        const incompleteNormMessage = this.renderIncompleteNormMessage();
        const setGoalButton = this.renderSetGoalButton();
        const actionPlan = this.renderActionPlan();
        const actionPlanDivider = this.renderActionPlanDivider();

        return (
            <div className="goal-setting-form">
                <ReactLoader loaded={this.isDataLoaded()}>
                    {performanceMessage}
                    <p key="set-a-goal" id={`set-a-goal`} className="set-a-goal">
                        Set a goal by:
                    </p>
                    {ritInputs}
                    {percentileInputs}
                    <p key="comparisons-section-header" className="comparisons-section-header">
                        Achievement and growth comparisons:
                    </p>
                    {ritComparisons}
                    {percentileComparisons}
                    {incompleteNormMessage}
                    {actionPlanDivider}
                    <h3 key="action-plan-section-header">Action Plan (optional)</h3>
                    {actionPlan}
                    {setGoalButton}
                </ReactLoader>
            </div>
        );
    },
});